import {
  createCode as apiCreateCode,
  deleteCode as apiDeleteCode,
  getGroup,
  orUndefined,
  updateCode,
} from '@lms/api-client/backend';
import { CODE_TABLES, TABLE_TO_CODE_TYPE } from './codes';

export interface AddCodeInput {
  curriculum: number;
  group: number;
  location: number;
  name: string;
}

export type AddCodeResult =
  | { ok: true }
  | { ok: false; messageKey: 'failed_to_create_code' }
  | { ok: false; message: string };

// Ports features/codes/actions.ts `addCode`: a server action wired to a form
// becomes a plain async function that calls the backend and returns a result
// the caller acts on. Revalidation (`revalidatePath`) is handled by the caller
// via SWR `mutate` after `ok`.
export async function addCode(input: AddCodeInput): Promise<AddCodeResult> {
  try {
    let codeTable = 22;
    const { curriculum, group: groupId, location: locationId, name } = input;

    let group: { id: number | undefined; locationId: number } | undefined;
    if (groupId === -1) {
      codeTable = CODE_TABLES['tenant-code'];
      group = { id: undefined, locationId };
    } else if (groupId === -2) {
      codeTable = CODE_TABLES['custom-supplier-code'];
      group = { id: undefined, locationId };
    } else if (groupId === -3) {
      codeTable = CODE_TABLES['supplier-code'];
      group = { id: undefined, locationId };
    } else {
      group = await orUndefined(getGroup(groupId));
    }
    if (group === undefined) {
      return { ok: false, messageKey: 'failed_to_create_code' };
    }

    const codeType = TABLE_TO_CODE_TYPE[codeTable];
    if (!codeType) {
      return { ok: false, messageKey: 'failed_to_create_code' };
    }

    await apiCreateCode({
      curriculumId: curriculum,
      locationId: group.locationId,
      codeType,
      groupId: group.id,
      name: name || undefined,
    });

    return { ok: true };
  } catch (e) {
    console.error(e);
    return { ok: false, message: `${e}` };
  }
}

export type RemoveCodeResult = { ok: true } | { ok: false; error: string };

// Ports features/codes/actions.ts `removeCode`. The Next version revalidated the
// path and issued a redirect to refresh; here the caller revalidates via SWR
// `mutate` once the deletion succeeds.
export async function removeCode(id: number): Promise<RemoveCodeResult> {
  try {
    await apiDeleteCode(id);
    return { ok: true };
  } catch (e) {
    return { ok: false, error: `${e}` };
  }
}

export type RenameCodeResult = { ok: true } | { ok: false; error: string };

// An empty name clears it; the backend trims and stores NULL.
export async function renameCode(
  id: number,
  name: string,
): Promise<RenameCodeResult> {
  try {
    await updateCode(id, { name: name.trim() || null });
    return { ok: true };
  } catch (e) {
    return { ok: false, error: `${e}` };
  }
}
